import { useEffect, useState, useRef } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@reach/tabs'
import { useActions } from 'statux'

import Image from '../../../components/Image/Image'
import ClipboardButton from '../../../components/ClipboardButton/ClipboardButton'

import authProvider from '../../../utils/auth-provider'
import { getCssVar } from '../../../utils/css'
import { graphqlClient, gql } from '../../../utils/gql'
import { setDocumentTitle } from '../../../utils/dom'

export default function DashboardProfile (props = {}) {
    const params = useParams()

    const history = useHistory()

    const setModal = useActions('modal')

    const companyLogo = useRef()

    const [companyLogoDimensions, setCompanyLogoDimensions]
        = useState((history.location.state || {}).companyLogoDimensions
                || { width: null, height: null })

    const cachedProfile = (history.location.state || {}).profile

    const [profile, setProfile] = useState(cachedProfile
            && (cachedProfile.user || {}).id === params.userId
        ? cachedProfile
        : null)

    const userId = (authProvider.getIdentity() || {}).id

    const isMe = params.userId === userId

    useEffect(async () => {
        const queryProfile = await graphqlClient
            .request(gql`
                query {
                    profiles (
                        limit: 1,
                        where: {
                            user: { id: { _eq: "${params.userId}" } }
                        }
                    ) {
                        id
                        email
                        role
                        approved
                        first_name
                        last_name
                        bio
                        title
                        company
                        company_bio
                        company_logo
                        company_website
                        company_goals
                        company_goals_public
                        company_materials
                        linkedin
                        type
                        details
                        user {
                            id
                            display_name
                            avatar_url
                        }
                    }
                }`)
            .then(obj => (obj.profiles || [])[0])
            .catch((err) => {
                console.warn(err)
                return false
            })

        if ((((queryProfile || {}).user || {}).avatar_url || '')
            .match(/\.gravatar\.com/))
            queryProfile.user.avatar_url = null

        if (queryProfile
                && (!profile
                    || JSON.stringify(profile)
                        !== JSON.stringify(queryProfile))) {
            setProfile(queryProfile)

            history.replace(
                `${history.location.pathname}${history.location.search}`,
                Object.assign(history.location.state || {}, {
                    profile: queryProfile
                }))
        }
    }, [])

    useEffect(() => {
        if (!profile)
            return profile === false
                && history.push('/dashboard')

        const title = profile.role === 'member'
            ? profile.company
            : (profile.user || {}).display_name

        if (title)
            setDocumentTitle(title)
    }, [profile])

    useEffect(() => {
        if (!companyLogo.current
                || (companyLogo.current.src || '')
                    .match(/^data:image\/png:base64/))
            return

        setCompanyLogoDimensions({
            width: companyLogo.current.naturalWidth
                || companyLogo.current.clientWidth,
            height: companyLogo.current.naturalHeight
                || companyLogo.current.clientHeight
        })

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                profile,
                companyLogoDimensions
            }))
    }, [companyLogo.current])

    if (!profile)
        return null

    const getProfileFullName = (obj = profile) => `${
        obj.first_name || ''} ${
        obj.last_name || ''}`
        .trim()

    const getProfileNameInitials = (obj = profile) => `${
        (obj.first_name || '').substring(0, 1)}${
        (obj.last_name || '').substring(0, 1)}`
        .trim()
        .toUpperCase()

    const renderWebsiteLink = (url = profile.company_website) => {
        if (!url)
            return null

        const text = url
            .replace(/^.+:\/\/(www\.*)*/, '')
            .replace(/\/$/, '')

        const textParts = text.split('')
            .map(str => str)

        const textSuffix = textParts[textParts.length - 1]

        const textPrefix = textParts
            .slice(0, textParts.length - 1)
            .join('') || ''

        return (
            <a
                className="body-6 display-inline-block text-decoration-hover dashboard-profile-link dashboard-profile-link-url"
                href={url}
                rel="noopener noreferrer"
                target="_blank"
                title="Opens in a new tab"
                style={{
                    color: getCssVar('--oxford')
                }}
            >
                {textPrefix
                    .replace(/\/([^/]+?)$/, '/ $1')
                    .split(' ')
                    .map((str, idx) => (
                        <span key={`${text}-${idx}`}>
                            {Boolean(idx)
                                && <wbr />}
                            {str}
                        </span>
                    ))}
                <span className="text-nowrap">
                    {textSuffix}
                    <svg
                        className="icon-launch"
                        width="16"
                        height="16"
                        style={{
                            marginLeft: '4px'
                        }}
                    >
                        <use xlinkHref="#icon-launch" />
                    </svg>
                </span>
            </a>
        )
    }

    const renderEmailLink = (email = profile.email) => {
        if (!email)
            return null

        const textParts = email.split('')
            .map(str => str)

        const textPrefix = textParts[0]

        const textSuffix = textParts
            .slice(1)
            .join('') || ''

        return (
            <a
                className="body-6 display-inline-block text-decoration-hover dashboard-profile-link dashboard-profile-link-email"
                href={`mailto:${email}`}
                rel="noopener noreferrer"
                target="_blank"
                title="Opens in default email client"
                style={{
                    color: getCssVar('--oxford')
                }}
            >
                <span className="text-nowrap">
                    <svg
                        className="icon-mail-outline"
                        width="18"
                        height="18"
                        style={{
                            marginRight: '4px'
                        }}
                    >
                        <use xlinkHref="#icon-mail-outline" />
                    </svg>
                    {textPrefix}
                </span>
                {textSuffix
                    .replace('@', ' @')
                    .split(' ')
                    .map((str, idx) => (
                        <span key={`${email}-${idx}`}>
                            {Boolean(idx)
                                && <wbr />}
                            {str}
                        </span>
                    ))}
            </a>
        )
    }

    const renderMemberProfile = () => {
        const path = `${history.location.pathname.split(params.userId)[0]}${
            params.userId}`

        const tabs = [{
            slug: 'overview',
            title: 'Overview',
            path: path
        }, {
            slug: 'team',
            title: 'Team',
            path: `${path}/team`
        }]

        const getActiveTab = () => tabs
            .filter((obj, idx) => params.tab === obj.slug)[0]
                || tabs[0]

        const getActiveIndex = () => tabs.indexOf(getActiveTab())

        const onTabChange = (e) => {
            const tab = tabs[e]

            if (!tab.path
                    || tab.path === history.location.pathname
                        .replace(/\/$/, ''))
                return

            history.push(tab.path, {
                profile,
                companyLogoDimensions
            })
        }

        const renderOverview = () => {
            const bioParts = (profile.company_bio || '')
                .replace(/\r/g, '')
                .split(/\n+/)
                .map(str => str)
                .filter(str => str)

            const goalsParts = profile.company_goals
                    && (profile.company_goals_public
                        || isMe
                        || authProvider.getDefaultRole() !== 'member')
                ? (profile.company_goals || '')
                    .replace(/\r/g, '')
                    .split(/\n+/)
                    .map(str => str)
                : []

            const materials = Object.keys(profile.company_materials || {})
                .filter((key, idx) => profile.company_materials[key].public
                    && profile.company_materials[key].url)
                .map((key) => ({
                    type: key,
                    url: profile.company_materials[key].url,
                    public: profile.company_materials[key].public
                }))

            return (
                <div className="row flex-justify-content-space-between">
                    <div
                        className={`col${
                            materials.length
                                    && (bioParts.length
                                        || goalsParts.length)
                                ? ' col-7-lg-up'
                                : ''}`}
                    >
                        {bioParts
                            .map((str, idx) => (
                                <p
                                    className={idx + 1 !== bioParts.length
                                        ? 'margin-b'
                                        : null}
                                    key={`dashboard-profile-company-bio-${idx}`}
                                >
                                    {str}
                                </p>))}
                        {Boolean(goalsParts.length)
                            && (
                                <>
                                    <h2
                                        className={`heading-5 margin-1-b${
                                            bioParts.length
                                                ? ' margin-4-t'
                                                : ''}`}
                                    >
                                        Our Catapult goals
                                    </h2>
                                    {goalsParts
                                        .map((str, idx) => (
                                            <p
                                                className={idx + 1
                                                        !== goalsParts.length
                                                    ? 'margin-b'
                                                    : null}
                                                key={`dashboard-profile-company-goals-${idx}`}
                                            >
                                                {str}
                                            </p>))}
                                </>)}
                        {!bioParts.length
                            && !goalsParts.length
                            && (
                                <div className="dashboard-profile-member-overview-empty pad-5-t pad-5-b pad-2-l pad-2-r pad-4-l-md-up pad-4-r-md-up">
                                    <p>
                                        {isMe
                                            ? 'Add company details by completing your profile.'
                                            : 'No company overview provided.'}
                                    </p>
                                    {isMe
                                        && (
                                            <div className="margin-3-t">
                                                <Link
                                                    to="/dashboard/settings/"
                                                    className="btn-outline-secondary-alt btn-sm"
                                                >
                                                    <svg
                                                        className="icon-person"
                                                        width="16"
                                                        height="16"
                                                    >
                                                        <use xlinkHref="#icon-person" />
                                                    </svg>
                                                    Complete my profile
                                                </Link>
                                            </div>)}
                                </div>)}
                    </div>
                    {Boolean(materials.length)
                        && (
                            <aside className="col-12 col-4-lg col-3-xl-up margin-4-t-lg-down">
                                <div>
                                    <ul
                                        className="row flex-justify-content-center-sm-down"
                                    >
                                        {materials
                                            .map((obj, idx) => (
                                                <li
                                                    key={`materials-${idx}`}
                                                    className={`col-12-sm-down col-12-lg-up display-flex${
                                                        materials.length === 1
                                                            ? ' col-6'
                                                            : ' col'}${
                                                        idx
                                                            ? ' margin-4-t-sm-down margin-4-t-lg-up'
                                                            : ''}`}
                                                >
                                                    <a
                                                        href={obj.url}
                                                        className="display-block text-decoration-hover body-4"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Opens in a new tab"
                                                        style={{
                                                            width: '100%',
                                                            color: getCssVar('--navy')
                                                        }}
                                                        onClick={(e) => {
                                                            if (obj.type !== 'promotionalVideo')
                                                                return

                                                            e.preventDefault()

                                                            setModal({
                                                                embed: obj.url,
                                                                title: `${profile.company} explainer video`
                                                            })
                                                        }}
                                                    >
                                                        <img
                                                            className="display-block margin-b"
                                                            src={`/images/cover-${
                                                                obj.type === 'pitchDeck'
                                                                    ? 'pitch-deck.svg'
                                                                    : (obj.type === 'leanCanvas'
                                                                        ? 'lean-canvas.png'
                                                                        : 'promotional-video.svg')}`}
                                                            style={{
                                                                width: '100%',
                                                                height: 'auto'
                                                            }}
                                                            alt=""
                                                        />
                                                        <span className="display-block">
                                                            <svg
                                                                width="16"
                                                                height="16"
                                                                style={{
                                                                    display: 'inline-block',
                                                                    marginRight: 4,
                                                                    top: 3
                                                                }}
                                                            >
                                                                <use
                                                                    xlinkHref={`#icon-${obj.type === 'promotionalVideo'
                                                                        ? 'play-circle'
                                                                        : 'picture-as-pdf'}`}
                                                                />
                                                            </svg>
                                                            <span>
                                                                {obj.type === 'pitchDeck'
                                                                    ? 'Pitch deck'
                                                                    : (obj.type === 'leanCanvas'
                                                                        ? 'Lean canvas'
                                                                        : 'Explainer video')}
                                                            </span>
                                                        </span>
                                                    </a>
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            </aside>)}
                </div>
            )
        }

        const renderTeam = () => {
            const bioParts = (profile.bio || '')
                .replace(/\r/g, '')
                .split(/\n+/)

            return (
                <ul className="row">
                    <li className="col-12">
                        <div className="row">
                            <div className="col-auto pad-1-r pad-3-b">
                                <div
                                    className="ratio-square round"
                                    style={{
                                        backgroundColor: !profile.user.avatar_url
                                            ? getCssVar('--mystic')
                                            : null
                                    }}
                                >
                                    <div className="display-flex flex-grow flex-justify-content-center flex-align-items-center">
                                        {!profile.user.avatar_url
                                            && getProfileNameInitials()}
                                        {profile.user.avatar_url
                                            && (
                                                <Image
                                                    src={`${profile.user.avatar_url}?w=256`}
                                                    alt={`Avatar of ${profile.user.display_name}`}
                                                    style={{ position: 'absolute' }}
                                                    data-object-fit
                                                />)}
                                    </div>
                                </div>
                                {isMe
                                    && (
                                        <div className="pad-1-t-lg-up text-align-center">
                                            <Link
                                                to="/dashboard/settings"
                                                className="body-4"
                                            >
                                                Edit
                                            </Link>
                                        </div>
                                    )}
                            </div>
                            <div className="col body-3 pad-3-b pad-4-r-lg-up">
                                <h2
                                    className="heading-5 font-weight-normal"
                                    aria-describedby="dashboard-profile-bio-title"
                                >
                                    {getProfileFullName()}
                                </h2>
                                {profile.title
                                    && (
                                        <p
                                            id="dashboard-profile-bio-title"
                                            style={{ color: getCssVar('--slate') }}
                                        >
                                            {profile.title}
                                        </p>)}
                                {(profile.company_website
                                        || profile.email)
                                    && (
                                        <ul className="pad-t">
                                            {profile.linkedin
                                                && (
                                                    <li className="display-flex">
                                                        {renderWebsiteLink(
                                                            profile.linkedin)}
                                                    </li>
                                                )}
                                            {profile.email
                                                && (
                                                    <li className="display-flex">
                                                        {renderEmailLink()}
                                                    </li>
                                                )}
                                        </ul>)}
                            </div>
                            <div className="col-12 col-6-lg col-7-xl-up">
                                {bioParts
                                    .map((str, idx) => (
                                        <p
                                            className={idx + 1 !== bioParts.length
                                                ? 'margin-b'
                                                : null}
                                            key={`dashboard-profile-bio-${idx}`}
                                        >
                                            {str}
                                        </p>
                                    ))}
                            </div>
                        </div>
                    </li>
                </ul>
            )
        }

        return (
            <Tabs
                className="tabs"
                defaultIndex={getActiveIndex()}
                index={getActiveIndex()}
                onChange={onTabChange}
            >
                <div className="tablist-container tablist-container-horizontal">
                    <TabList>
                        {tabs
                            .map((obj, idx) => (
                                <Tab
                                    key={`dashboard-profile-tab-${idx}`}
                                    className="btn-unstyled"
                                >
                                    {obj.title}
                                </Tab>
                            ))}
                    </TabList>
                </div>
                <TabPanels className="body-3">
                    {tabs
                        .map((obj, idx) => (
                            <TabPanel
                                className={`dashboard-profile-tabpanel-${
                                    obj.slug}`}
                                key={`dashboard-profile-tabpanel-${idx}`}
                            >
                                {obj.slug === 'overview'
                                    ? renderOverview()
                                    : renderTeam()}
                            </TabPanel>
                        ))}
                </TabPanels>
            </Tabs>
        )
    }

    const renderAdvisorProfile = () => (
        <>
            {profile.bio
                && (
                    <section className="dashboard-profile-advisor-bio pad-0-t pad-5-b">
                        <div className="pad-3-b-lg-up">
                            <div className="row">
                                <div className="col-12 col-7-lg-up">
                                    <h2 className="heading-5 font-weight-normal margin-1-b">About me</h2>
                                    {(profile.bio || '')
                                        .replace(/\r/g, '')
                                        .split(/\n+/)
                                        .map((str, idx) => (
                                            <p key={`dashboard-profile-bio-${idx}`}>
                                                {str}
                                            </p>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </section>)}
            {profile.company
                && (
                    <section className="dashboard-profile-advisor-company pad-5-t pad-5-b">
                        <div className="container">
                            <div
                                className="row flex-nowrap"
                                style={{ zIndex: 1 }}
                            >
                                <div className="col-auto col-2-lg-up display-flex flex-justify-content-center-lg-up pad-1-r-lg-down">
                                    <div className="display-flex flex-direction-column">
                                        <div
                                            className={`dashboard-profile-advisor-company-logo${
                                                !profile.company_logo
                                                    ? ' ratio-square round'
                                                    : ''}`}
                                            style={{
                                                backgroundColor: !profile.company_logo
                                                    ? getCssVar('--mystic')
                                                    : null
                                            }}
                                        >
                                            <div className="display-flex flex-grow flex-justify-content-center flex-align-items-center">
                                                {!profile.company_logo
                                                    && (
                                                        <svg
                                                            className="icon-extension flex-none"
                                                            width="36"
                                                            height="36"
                                                        >
                                                            <use xlinkHref="#icon-extension" />
                                                        </svg>)}
                                                {profile.company_logo
                                                    && (
                                                        <Image
                                                            imgRef={(ref) => {
                                                                companyLogo.current
                                                                    = ref
                                                            }}
                                                            src={`${profile.company_logo}?w=192`}
                                                            alt={`${profile.company_name
                                                                || 'company'} logo`}
                                                            width={companyLogoDimensions.width}
                                                            height={companyLogoDimensions.height}
                                                        />)}
                                            </div>
                                        </div>
                                        {isMe
                                            && (
                                                <div className="text-align-center">
                                                    <Link
                                                        to="/dashboard/settings/company"
                                                        className="body-4"
                                                    >
                                                        Edit
                                                    </Link>
                                                </div>
                                            )}
                                    </div>
                                </div>
                                <div
                                    className={`col col-7-lg-up display-flex flex-direction-column flex-justify-content-center-lg-down ${
                                        isMe
                                            ? ' pad-3-b-lg-down'
                                            : ' pad-1-b-lg-down'}`}
                                >
                                    <div>
                                        <h2 className="heading-5 font-weight-normal">{profile.company}</h2>
                                        {renderWebsiteLink()}
                                    </div>
                                    <div className="display-none-lg-down pad-1-t">
                                        {Boolean(profile.company_bio)
                                            && profile.company_bio
                                                .replace(/\r/g, '')
                                                .split(/\n+/)
                                                .map((str, idx) => (
                                                    <p key={`dashboard-profile-comapny-bio-lg-${idx}`}>
                                                        {str}
                                                    </p>
                                                ))}
                                        {!profile.company_bio
                                            && (
                                                <p>
                                                    {isMe
                                                        ? 'Add company details by completing your profile.'
                                                        : 'No company description provided'}
                                                </p>)}
                                        {!profile.company_bio
                                            && isMe
                                            && (
                                                <div className="margin-3-t">
                                                    <Link
                                                        to="/dashboard/settings/"
                                                        className="btn-outline-secondary-alt btn-sm"
                                                    >
                                                        <svg
                                                            className="icon-person"
                                                            width="16"
                                                            height="16"
                                                        >
                                                            <use xlinkHref="#icon-person" />
                                                        </svg>
                                                        Complete my profile
                                                    </Link>
                                                </div>)}
                                    </div>
                                </div>
                            </div>
                            <div className="display-none-lg-up pad-1-t">
                                {Boolean(profile.company_bio)
                                    && profile.company_bio
                                        .replace(/\r/g, '')
                                        .split(/\n+/)
                                        .map((str, idx) => (
                                            <p key={`dashboard-profile-comapny-bio-sm-${idx}`}>
                                                {str}
                                            </p>
                                        ))}
                                {!profile.company_bio
                                    && (
                                        <p>
                                            {isMe
                                                ? 'Add company details by completing your profile.'
                                                : 'No company description provided'}
                                        </p>)}
                                {!profile.company_bio
                                    && isMe
                                    && (
                                        <div className="margin-3-t">
                                            <Link
                                                to="/dashboard/settings/"
                                                className="btn-outline-secondary-alt btn-sm"
                                            >
                                                <svg
                                                    className="icon-person"
                                                    width="16"
                                                    height="16"
                                                >
                                                    <use xlinkHref="#icon-person" />
                                                </svg>
                                                Complete my profile
                                            </Link>
                                        </div>)}
                            </div>
                        </div>
                    </section>)}
        </>
    )

    return (
        <div
            className={`dashboard-profile dashboard-profile-${
                profile.role} pad-5-b pad-5-t-lg-up`}
        >
            <div className="container">
                <div className="row flex-justify-content-center">
                    <div className="col-12 col-10-xl-up">
                        <div className="card card-offscreen-x-lg-down pad-4-t-lg-down pad-5-t-lg-up pad-5-b-lg-up">
                            <div className="dashboard-profile-header margin-4-b margin-5-b-lg-up">
                                <div
                                    className={`row${
                                        profile.role === 'member'
                                            ? ' flex-align-items-start'
                                            : ''}`}
                                >
                                    <div
                                        className={`col-12 col-auto-lg-up margin-b-lg-down display-flex-lg-down flex-direction-column flex-justify-content-center${
                                            profile.role !== 'advisor'
                                                ? ' margin-auto margin-0-l'
                                                : ''}`}
                                    >
                                        <div
                                            className={`dashboard-profile-header-avatar${
                                                (profile.role === 'advisor'
                                                        || (profile.role === 'member'
                                                            && !profile.company_logo))
                                                    ? ' ratio-square round'
                                                    : ''}`}
                                            style={{
                                                backgroundColor: profile.role === 'advisor'
                                                        || (profile.role === 'member'
                                                            && !profile.company_logo)
                                                    ? getCssVar('--mystic')
                                                    : null
                                            }}
                                        >
                                            <div className="display-flex flex-grow flex-justify-content-center flex-align-items-center">
                                                {profile.role === 'advisor'
                                                    && !profile.user.avatar_url
                                                    && getProfileNameInitials()}
                                                {profile.role === 'advisor'
                                                    && profile.user.avatar_url
                                                    && (
                                                        <Image
                                                            src={`${profile.user.avatar_url}?w=384`}
                                                            alt={`Avatar of ${profile.user.display_name}`}
                                                            style={{ position: 'absolute' }}
                                                            data-object-fit
                                                        />)}
                                                {profile.role === 'member'
                                                    && !profile.company_logo
                                                    && (
                                                        <svg
                                                            className="icon-extension"
                                                            width="36"
                                                            height="36"
                                                        >
                                                            <use xlinkHref="#icon-extension" />
                                                        </svg>)}
                                                {profile.role === 'member'
                                                    && profile.company_logo
                                                    && (
                                                        <Image
                                                            imgRef={(ref) => {
                                                                companyLogo.current
                                                                    = ref
                                                            }}
                                                            src={`${profile.company_logo}?w=256`}
                                                            alt={`${profile.company || 'company'} logo`}
                                                            width={companyLogoDimensions.width}
                                                            height={companyLogoDimensions.height}
                                                        />)}
                                            </div>
                                        </div>
                                        {isMe
                                            && (
                                                <div
                                                    className="pad-1-t display-flex flex-justify-content-center-lg-up margin-0"

                                                >
                                                    <Link
                                                        to={`/dashboard/settings${
                                                            profile.role === 'member'
                                                                ? '/company'
                                                                : ''}`}
                                                        className="body-4"
                                                    >
                                                        Edit
                                                    </Link>
                                                </div>
                                            )}
                                    </div>
                                    <div className="col col-12-md-down margin-3-b-lg-down">
                                        <h1
                                            className="heading-2"
                                            aria-describedby="dashboard-profile-type"
                                            style={{
                                                color: getCssVar('--navy')
                                            }}
                                        >
                                            {profile.role === 'member'
                                                ? profile.company
                                                : getProfileFullName()}
                                        </h1>
                                        {profile.role === 'member'
                                            && (
                                                <div className="display-flex">
                                                    {profile.company_website
                                                        && renderWebsiteLink()}
                                                </div>)}
                                        {profile.type
                                            && (
                                                <div className="display-flex pad-1-t">
                                                    <p
                                                        id="dashboard-profile-type"
                                                        className="body-6 display-inline-flex"
                                                        style={{ color: getCssVar('--navy') }}
                                                    >
                                                        <svg
                                                            className="icon-ladder"
                                                            width="16"
                                                            height="16"
                                                            style={{
                                                                marginRight: '4px'
                                                            }}
                                                        >
                                                            <use xlinkHref="#icon-ladder" />
                                                        </svg>
                                                        {profile.type}
                                                    </p>
                                                </div>)}
                                        {profile.role === 'advisor'
                                            && (profile.title
                                                || profile.company
                                                || profile.email
                                                || profile.linkedin)
                                            && (
                                                <div className="row body-3">
                                                    {(profile.title
                                                            || profile.company)
                                                        && (
                                                            <div className="col-auto col-12-lg-down pad-t">
                                                                {profile.title
                                                                    && <p>{profile.title}</p>}
                                                                {profile.company
                                                                    && <p>{profile.company}</p>}
                                                            </div>)}
                                                    {(profile.email
                                                            || profile.linkedin)
                                                        && (
                                                            <div className="col pad-t">
                                                                {profile.email
                                                                    && (
                                                                        <p>
                                                                            {renderEmailLink()}
                                                                        </p>)}
                                                                {profile.linkedin
                                                                    && (
                                                                        <p>
                                                                            {renderWebsiteLink(
                                                                                profile
                                                                                    .linkedin)}
                                                                        </p>)}
                                                            </div>)}
                                                </div>)}
                                    </div>
                                    <div
                                        className={`col-auto col-12-lg-down${
                                            profile.role === 'advisor'
                                                ? ' pad-5-t-lg-up'
                                                : ''}`}
                                    >
                                        <ClipboardButton
                                            data-clipboard-text={`${window.location.origin}${
                                                history.location.pathname.replace(new RegExp(
                                                    `(\\/${params.userId})\\/*.*$`), '$1')}`}
                                            className="btn-outline-secondary-alt btn-sm"
                                            icon="icon-link"
                                            successText="Link copied"
                                        >
                                            <span>Share this profile</span>
                                        </ClipboardButton>
                                    </div>
                                </div>
                            </div>
                            {profile.role === 'member'
                                ? renderMemberProfile()
                                : renderAdvisorProfile()}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}